import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { X, Mail, Phone, ArrowRightLeft } from "lucide-react";
import OwnerTransferForm from "./OwnerTransferForm";
import OwnerHistory from "./OwnerHistory";

const INPUT_STYLE = { background: "var(--vault-bg)", border: "1px solid var(--vault-border)" };

export default function OwnerModal({ code, onClose, onSaved }) {
  const [email, setEmail] = useState(code.owner_email || "");
  const [phone, setPhone] = useState(code.owner_phone || "");
  const [saving, setSaving] = useState(false);
  const [transferring, setTransferring] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/admin/codes/${code.id}`, { owner_email: email.trim(), owner_phone: phone.trim() });
      toast.success("Owner contact saved");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.75)" }} data-testid="owner-modal" onClick={onClose}>
      <div className="glass rounded-2xl p-8 max-w-md w-full space-y-5 fade-up max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
      <form onSubmit={save} className="space-y-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="overline text-[var(--vault-text-muted)]">Bottle owner</p>
            <h3 className="font-display text-2xl mt-1">{code.label || code.code}</h3>
            <p className="text-sm text-[var(--vault-text-muted)] mt-1 font-mono-vault">{code.code}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-white/5" data-testid="owner-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-[var(--vault-text-muted)]">
          Access codes for this bottle are always sent here. Only an administrator can change these details. Use this form to correct details; use Transfer when the bottle changes hands.
        </p>
        <div>
          <label className="overline block text-[var(--vault-text-muted)] mb-2 inline-flex items-center gap-1"><Mail className="w-3 h-3" /> Owner email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="owner@example.com" className="w-full px-3 py-2.5 rounded outline-none text-sm" style={INPUT_STYLE} data-testid="owner-email-input" />
        </div>
        <div>
          <label className="overline block text-[var(--vault-text-muted)] mb-2 inline-flex items-center gap-1"><Phone className="w-3 h-3" /> Owner phone</label>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+351 912 345 678" className="w-full px-3 py-2.5 rounded outline-none text-sm" style={INPUT_STYLE} data-testid="owner-phone-input" />
          <p className="text-[10px] text-[var(--vault-text-faint)] mt-1">Access codes can be sent here by SMS (international format).</p>
        </div>
        <div className="flex justify-between gap-2 pt-2">
          <button type="button" onClick={() => setTransferring(true)} disabled={transferring} className="btn-ghost inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm" data-testid="owner-transfer-btn">
            <ArrowRightLeft className="w-3.5 h-3.5" /> Transfer
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="owner-save-btn" className="btn-gold px-5 py-2 rounded-md text-sm">
              {saving ? <span className="spin" /> : "Save"}
            </button>
          </div>
        </div>
      </form>
        {transferring && <OwnerTransferForm code={code} onDone={onSaved} onCancel={() => setTransferring(false)} />}
        <div className="pt-4 border-t" style={{ borderColor: "var(--vault-border)" }}>
          <OwnerHistory code={code} />
        </div>
      </div>
    </div>
  );
}
